/**
 * Variables and the paths they read. A judge prompt declares its variables with
 * `{{name}}`; a code check declares its own. Each maps to a path into the sample
 * document (`input.question`, `expected_output`, `output`, `trace.tool_calls`…).
 */

import type { PathInfo } from "./types";

const VARIABLE = /\{\{\s*([A-Za-z_][\w-]*)\s*\}\}/g;

/** The `{{name}}` placeholders in a judge prompt, in order, each once. */
export function promptVariables(prompt: string): string[] {
  return [...new Set([...prompt.matchAll(VARIABLE)].map((m) => m[1]!))];
}

/** The prompt cut into plain text and `{{variable}}` pieces, for highlighting. */
export function promptPieces(prompt: string): Array<{ text: string; variable: boolean }> {
  const out: Array<{ text: string; variable: boolean }> = [];
  let at = 0;
  for (const m of prompt.matchAll(VARIABLE)) {
    if (m.index > at) out.push({ text: prompt.slice(at, m.index), variable: false });
    out.push({ text: m[0], variable: true });
    at = m.index + m[0].length;
  }
  if (at < prompt.length) out.push({ text: prompt.slice(at), variable: false });
  return out;
}

const GUESS: Record<string, string> = {
  input: "input",
  question: "input",
  output: "output",
  answer: "output",
  response: "output",
  expected_output: "expected_output",
  expected: "expected_output",
  reference: "expected_output",
  ground_truth: "expected_output",
  context: "context",
  keywords: "keywords",
  expected_tools: "expected_tools",
  tool_calls: "trace.tool_calls",
  retrieved_context: "trace.retrieved_context",
  instructions: "agent.instructions",
  task: "agent.instructions",
  output_schema: "agent.output_schema",
};

/** A first guess for a new prompt variable, until the person changes it. */
export function guessPath(name: string): string {
  return GUESS[name] ?? `input.${name}`;
}

/** "in 80 of 100 items" when not every item has the path; "" otherwise. */
export function coverage(p: Pick<PathInfo, "present_in" | "total">): string {
  return p.total > 0 && p.present_in < p.total ? `in ${p.present_in} of ${p.total} items` : "";
}

/** `{{var}}: path` lines, for a hover title. */
export function mappingLines(mapping: Record<string, string | null>): string {
  return Object.entries(mapping)
    .map(([v, p]) => `{{${v}}}: ${p ?? "not mapped"}`)
    .join("\n");
}

const HEADS: Record<string, string> = { expected_output: "Expected output", meta: "Metadata" };

/** A path as breadcrumbs, Langfuse-style: `input.messages[0].content` →
 *  `["Input", "messages", "[0]", "content"]`. */
export function crumbs(path: string): string[] {
  const parts = path
    .replace(/\[(\d+)\]/g, ".[$1]")
    .split(".")
    .filter(Boolean);
  const [head, ...rest] = parts;
  return head ? [HEADS[head] ?? head[0]!.toUpperCase() + head.slice(1), ...rest] : [];
}

const SOURCES: Record<string, string> = {
  input: "Input",
  expected_output: "Expected output",
  meta: "Metadata",
  output: "Output",
  trace: "From the agent's run",
  agent: "From the agent's setup",
  run: "Run settings",
  observation: "Raw observation",
};

/** Where a path's value comes from, to group the path list by. */
export function sourceOf(path: string): string {
  return SOURCES[path.split(/[.[]/, 1)[0]!] ?? "Other";
}
